import * as Configuration from '../app.constants.js';
export class XHRStatsService {

  constructor($injector) {
    this.$injector = $injector;
    // setupXhrTracking(this); // uncomment together with above class and init code to track raw xhr events
  }

  onLocalStorageOverflow(kv) {
    this.pushPending("local-storage-overflow", {
      kv: kv
    });
  }
  onLocalStorageClear(kv) {
    this.pushPending("local-storage-clear", {
      kv: kv
    });
  }
  onSetJwtToLocalStorage(jwt) {
    this.pushPending("local-storage-set-jwt", { jwt: jwt });
  }
  onLocalStorageException(operation, key, exception) {
    this.pushPending("local-storage-exception", { operation: operation, key: key, exception: '' + exception });
  }

  onXmlHttpRequest(type, uid, extra) {
    this.pushPending("xml-http-request-raw", {
      type: type,
      uid: uid,
      time: performance.now(),
      extra: extra
    });
  }

  onUnknownLocation(url) {
    this.pushPending('unknown-location', {
      url: url
    });
  }

  onException(exception, cause) {
    this.pushPending('exception', {
      exception: exception.toString(),
      cause: cause,
      backtrace: angular.isUndefined(exception.stack) ? 'Backtrace not available' : exception.stack
    });
  }

  onDomInitialize(root) {
    this.pushPending('dom-initialize', {
      'root': root
    });
  }
  onDomChange(change) {
    this.pushPending('dom-change', {
      'change': change
    });
  }

  _target(event) {
    let target = event.target.tagName;
    if (event.target.id) {
      target += '#' + event.target.id;
    } else if (event.target.hasAttribute('translate-once')) {
      target += '!translate-once="' + event.target.getAttribute('translate-once') + '"';
    } else if (event.target.hasAttribute('ng-click')) {
      target += '!ng-click="' + event.target.getAttribute('ng-click') + '"';
    } else if (event.target.parentNode && event.target.parentNode.hasAttribute && event.target.parentNode.hasAttribute('ng-click')) {
      target += '!parent.ng-click="' + event.target.parentNode.getAttribute('ng-click') + '"';
    } else if (event.target.children && event.target.children[0] && event.target.children[0].hasAttribute('translate-once')) {
      target += '!children[0].translate-once="' + event.target.children[0].getAttribute('translate-once') + '"';
    } else if (event.target.className) {
      // target.className is not a string if the target is an SVG element
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/className
      // https://developer.mozilla.org/en-US/docs/Web/API/SVGAnimatedString
      let className = event.target.className;
      if (typeof className.baseVal === 'string') {
        className = className.baseVal;
      }
      target += '.' + className.split(' ').join('.');
    }
    return target;
  }

  onOnLine() {
    this.pushPending("online", {});
  }
  onOffLine() {
    this.pushPending("offline", {});
  }

  onClick(event) {
    this.pushPending("mouse-click", {
      target: this._target(event),
      x: event.clientX,
      y: event.clientY
    });
  }

  onMouseOver(event) {
    this.pushOrAggregatePending('mouse-over', [Date.now(), event.clientX, event.clientY]);
  }

  onTouchStartOrEnd(event) {
    const loggedTouches = [];
    for (let i = 0; i < event.changedTouches.length; i++) {
      const touch = event.changedTouches[i];
      loggedTouches.push({
        'id': touch.identifier,
        'x': touch.clientX,
        'y': touch.clientY,
      });
    }
    this.pushPending(event.type, {
      'changedTouches': loggedTouches
    });
  }

  onKeyUp(event) {
    if (!(event.target.hasAttribute("type") && event.target.getAttribute("type") === "password")) {
      this.pushOrAggregatePending('keyboard', [Date.now(), event.key, event.keyCode]);
    }
  }

  onResize() {
    this.pushOrOverwritePending('resize', {
      width: window.innerWidth,
      height: window.innerHeight
    });
  }

  onError(error) {
    this.pushOrOverwritePending('error', { error: error });
  }

  onUnhandledrejection(exception) {
    this.pushOrOverwritePending('exception', { exception: exception.reason.message });
  }

  pushState(type, toState, toParams, fromState, fromParams) {
    let $state = this.$injector.get('$state');
    this.pushPending(type, {
      toUrl: $state.href(toState, toParams),
      toName: toState.name,
      toParams: toParams,
      fromUrl: $state.href(fromState, fromParams),
      fromName: fromState.name,
      fromParams: fromParams
    });
  }

  onStateChangeStart(toState, toParams, fromState, fromParams) {
    this.pushState('state-change-start', toState, toParams, fromState, fromParams);
  }
  onStateChangeSuccess(toState, toParams, fromState, fromParams) {
    this.pushState('state-change-success', toState, toParams, fromState, fromParams);
  }
  onStateChangeError(toState, toParams, fromState, fromParams, error) {
    let $state = this.$injector.get('$state');
    let description = null;
    let backtrace = null;
    try {
      description = error.toString();
      backtrace = error.stack;
    } catch (e) {
      console.error(e);
    }
    this.pushPending('state-change-error', {
      toUrl: $state.href(toState, toParams),
      toName: toState.name,
      toParams: toParams,
      fromUrl: $state.href(fromState, fromParams),
      fromName: fromState.name,
      fromParams: fromParams,
      errorDescription: description,
      errorBacktrace: backtrace
    });
  }

  onPerformanceEntries(entries) {
    for (let i = 0; i < entries.length; i++) {
      let entry = entries[i];
      if (typeof entry.name === 'string' && !entry.name.startsWith(Configuration.constants.xhrStatsUrl)) {
        this.pushPending("performance", { entry: entry })
      }
    }
  }

  onHttpRequest(request) {
    request.request_timestamp = Math.round(performance.now());
    request.request_stat_counter = this.stat_counter;
    this.pushPending('http-request', this.cloneHttpRequest(request));
  }

  onHttpRequestError(rejection) {
    // I haven't manage to get this handler called, perhaps because our interceptor is the first one to be called
    this.pushPending('http-request-error', { 'error': this.toStringMaxLength(rejection) });
  }

  onHttpResponse(response) {
    if (response && typeof response === 'object') {
      response.response_timestamp = Math.round(performance.now());
      this.pushPending('http-response', this.cloneHttpResponse(response));
    }
  }

  onHttpResponseError(rejection) {
    if (this.looksLikeResponse(rejection)) {
      rejection.response_timestamp = Math.round(performance.now());
      this.pushPending('http-response', this.cloneHttpResponse(rejection)); // let's treat a "status != 200" response just like a normal response
    } else {
      this.pushPending('http-response-error', { 'error': this.toStringMaxLength(rejection) });
    }
  }

  cloneHttpRequest(request) {
    let result = null;
    if (request && typeof request === 'object') {
      result = {
        'request_timestamp': request.request_timestamp,
        'request_stat_counter': request.request_stat_counter,
        'headers': request.headers,
        'method': request.method,
        'params': request.params,
        'url': request.url,
        'data': this.toStringMaxLength(request.data)
      };
    }
    return result;
  }

  cloneHttpHeaders(headersGetter) {
    let headerWhitelist = ['Content-Type', 'Content-Encoding', 'Content-Length', 'X-Alcmeon-Request-Id', 'Last-Modified', 'ETag', 'Cache-Control', 'Content-Language', 'Expires', 'Pragma'];
    let retval = {};
    for (let h of headerWhitelist) {
      let v = headersGetter(h);
      if (v !== undefined && v !== null) {
        retval[h] = v;
      }
    }
    return retval;
  }

  cloneHttpResponse(response) {
    let result = null;
    if (response && typeof response === 'object') {
      result = {
        'response_timestamp': response.response_timestamp,
        'headers': this.cloneHttpHeaders(response.headers),
        'status': response.status,
        'statusText': response.statusText,
        'data': this.toStringMaxLength(response.data),
        'config': this.cloneHttpRequest(response.config)
      };
    }
    return result;
  }

  looksLikeResponse(rejection) {
    return rejection && (typeof rejection.status === 'number') && rejection.config && (typeof rejection.config.url === 'string');
  }

  pushPending(type, event) {
    const angularTrackService = this._getAngularTrackService();
    if (angularTrackService) {
      angularTrackService.pushPending(type, event);
    }
    // ignore the few mouse-over and performance events that might show up before the downgraded module is available
  }

  pushOrAggregatePending(type, subEvent) {
    const angularTrackService = this._getAngularTrackService();
    if (angularTrackService) {
      angularTrackService.pushOrAggregatePending(type, subEvent);
    }
    // ignore the few mouse over events and the likes that might show up before the downgraded module is available
  }

  pushOrOverwritePending(type, event) {
    const angularTrackService = this._getAngularTrackService();
    if (angularTrackService) {
      angularTrackService.pushOrOverwritePending(type, event);
    }
    // ignore the few mouse over events and the likes that might show up before the downgraded module is available
  }
  _getAngularTrackService() {
    try {
      return this.$injector.get('AngularTrackService');
    } catch (e) {
      // Most likely Error while instantiating injectable 'TrackService': Trying to get the Angular injector before bootstrapping the corresponding Angular module.
      return null;
    }
  }

  clientSessionId() {
    const angularTrackService = this._getAngularTrackService();
    return angularTrackService ? angularTrackService.sessionId : null;
  }

  serverSessionId() {
    // use $injector to avoid circular dependency ($http -> XHRStats -> UserFactory -> $http)
    let loggedUser = this.$injector.get('UserFactory').getLoggedUser();
    return loggedUser && loggedUser.session_id ? loggedUser.session_id : 0;
  }
  logguedUserId() {
    // use $injector to avoid circular dependency ($http -> XHRStats -> UserFactory -> $http)
    let loggedUser = this.$injector.get('UserFactory').getLoggedUser();
    return loggedUser && loggedUser.id ? loggedUser.id : 0;
  }
  companyId() {
    // use $injector to avoid circular dependency ($http -> XHRStats -> UserFactory -> $http)
    let loggedUser = this.$injector.get('UserFactory').getLoggedUser();
    return loggedUser && loggedUser.companies ? loggedUser.companies[0].id : 0;
  }

  toStringMaxLength(value) {
    let result;
    try {
      result = JSON.stringify(value);
    } catch (error) {
      result = "probably a cyclic object";
    }

    if (result && result.length > 100) {
      result = result.substring(0, 100) + '\u2026';
    }
    return result;
  }


  static XHRStatsFactory($injector) {
    return new XHRStatsService($injector);
  }

}

XHRStatsService.XHRStatsFactory.$inject = ['$injector'];
